import t from 'react-translate';
import { css } from '@emotion/react';
import { MentorResult } from 'redux/actions/mentoring-program-participant-driven';
import {
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import Avatar from 'athena/components/mentoring-program/avatar';
import { gray2, gray4, gray6, success, warning, white } from 'styles/global_defaults/colors';
import NvIcon from 'shared/components/nv-icon';
import { MentorRequestSource } from 'redux/schemas/models/mentor-request';
import { AngularContext } from 'react-app';
import { useContext } from 'react';
import ClickableContainer from 'components/clickable-container';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { novoEdBlue } from 'athena/styles/colors';
import { config } from '@config/pendo.config.json';

export const CARD_WIDTH = 320;
const AVATAR_SIZE = 100;

type MentorCardProps = {
  mentor?: MentorResult;
};

type StatusProps = {
  isAvailable: boolean;
};

export const mentorCardStyles = css`
  flex-direction: column;
  width: ${CARD_WIDTH}px;
  height: 100%;
  border: 1px solid ${gray6};
  border-radius: ${halfSpacing}px;
  padding: ${standardSpacing}px ${largeSpacing}px;

  &:hover {
    box-shadow: 0px 2px 4px 0px rgba(58, 66, 77, 0.08);
    .mentor-name {
      color: ${novoEdBlue};
    }
  }

  .mentor-card-profile {
    gap: ${halfSpacing}px;

    .mentor-card-avatar {
      background-color: ${gray6};
    }
  }
  .mentor-card-profile-info {
    gap: ${quarterSpacing}px;

    .mentor-card-info-location {
      i {
        color: ${warning};
      }
    }
    .mentor-card-info-connection-requested {
      width: fit-content;
      color: ${white};
      background-color: ${gray2};
      padding: ${quarterSpacing}px 12px;
      border-radius: ${standardSpacing}px;
    }
  }
  .mentor-card-avatar {
    width: ${AVATAR_SIZE}px;
    height: ${AVATAR_SIZE}px;
    border-radius: 50%;
    aspect-ratio: 1 / 1;
  }
  .mentor-card-details {
    gap: ${quarterSpacing}px;

    .bio,
    .headline {
      height: fit-content;
      line-height: unset;
    }
  }
`;

const statusStyles = (color: string) => css`
  .status-indicator {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${color};
  }
`;

const Status = ({ isAvailable }: StatusProps) => {
  const translationBase =
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.FIND_A_MENTOR_MODAL
      .CONTENT.MENTOR_CARD.STATUS;
  const statusKey = isAvailable ? 'available' : 'unavailable';
  const statusMap = {
    available: {
      color: success,
      title: translationBase.AVAILABLE(),
    },
    unavailable: {
      color: gray4,
      title: translationBase.UNAVAILABLE(),
    },
  };

  const { color, title } = statusMap[statusKey];

  return (
    <div css={statusStyles(color)} className='d-flex align-items-center'>
      <div className='status-indicator mr-2' />
      <div className='status-title semi-bold black'>{title}</div>
    </div>
  );
};

const MentorCard = ({ mentor }: MentorCardProps) => {
  const {
    id: mentorId,
    user,
    availability,
    connectionRequested = false,
  } = mentor || {};
  const { fullName, displayLocation, headline, bio } = user || {};
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { setShowFindMentorModal } = mentoringProgram;
  const translationBase =
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.FIND_A_MENTOR_MODAL
      .CONTENT.MENTOR_CARD;

  const showMentorRequestModal = () => {
    const params = { source: MentorRequestSource.FIND_MENTOR, mentorId };
    setTimeout(() => setShowFindMentorModal(false), 0);
    $state.go($state.current, params, { notify: false });
  };

  const dataQATag =
    config.pendo.athena.mentorshipProgram.participantHome.findAMentor.modal
      .mentorCard;

  return (
    <ClickableContainer
      css={mentorCardStyles}
      onClick={showMentorRequestModal}
      data-qa={dataQATag}
      data-qa-id={`${dataQATag}-${mentorId}`}
    >
      <div className='d-flex mentor-card-profile'>
        <Avatar user={user} className='mentor-card-avatar' />
        <div className='d-flex flex-column mentor-card-profile-info align-content-center justify-content-center'>
          <Status isAvailable={availability > 0} />
          <div className='mentor-name heading-4 black'>{fullName}</div>
          {displayLocation && (
            <div className='d-flex align-items-center mentor-card-info-location'>
              <NvIcon icon='location' size='smallest' className='mr-2' />
              <span className='text-small semi-bold gray-2'>
                {displayLocation}
              </span>
            </div>
          )}
          {connectionRequested && (
            <div className='d-flex align-items-center mentor-card-info-connection-requested'>
              <NvIcon icon='dueday' size='xss-smallest' className='mr-1' />
              <span className='text-small bold'>
                {translationBase.CONNECTION_REQUESTED()}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className='d-flex flex-column mentor-card-details mt-2'>
        <div className='ellipsis truncated-text headline text-regular bold black'
        >
          {headline}
        </div>
        <div
          className='three-line-ellipsis truncated-text bio text-regular gray-1'
        >
          {bio}
        </div>
      </div>
    </ClickableContainer>
  );
};

export default MentorCard;
